/**
 * Client side of the public-signup anti-abuse challenge.
 *
 * The API only accepts newsletter / quiz-lead submissions that include a
 * signed, single-use `formToken` obtained from GET /api/signup/challenge.
 * The server rejects tokens younger than ~1.5s, so this helper caches a
 * token as soon as it's first requested and, if needed, waits out the
 * remaining minimum age before handing it to the caller.
 */

const MIN_TOKEN_AGE_MS = 1600;

interface CachedToken {
  token: string;
  issuedAt: number;
}

let cached: Promise<CachedToken> | null = null;

async function fetchToken(): Promise<CachedToken> {
  const res = await fetch(`${import.meta.env.BASE_URL}api/signup/challenge`);
  if (!res.ok) throw new Error("Could not get signup token");
  const data = (await res.json()) as { token?: string };
  if (!data.token) throw new Error("Could not get signup token");
  return { token: data.token, issuedAt: Date.now() };
}

/** Consume a challenge token, waiting out the server's minimum age if needed. */
export async function getSignupToken(): Promise<string> {
  const pending = cached ?? fetchToken();
  cached = null;
  let entry: CachedToken;
  try {
    entry = await pending;
  } catch {
    entry = await fetchToken();
  }
  const wait = MIN_TOKEN_AGE_MS - (Date.now() - entry.issuedAt);
  if (wait > 0) await new Promise((r) => setTimeout(r, wait));
  return entry.token;
}
